import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react'

export interface SelectOption {
  value: string
  label: ReactNode
  /** Optional accent colour rendered as a small dot (e.g. difficulty coding). */
  accent?: string
}

interface SelectProps {
  value: string
  options: SelectOption[]
  onChange: (value: string) => void
  ariaLabel?: string
}

// A custom themed dropdown — native <select> can't style its option list, so we
// render our own to match the rest of the site.
export default function Select({ value, options, onChange, ariaLabel }: SelectProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const selected = options.find((o) => o.value === value)

  return (
    <div className="tselect" ref={ref}>
      <button
        type="button"
        className="tselect-button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="tselect-value">
          {selected?.accent && (
            <span className="tselect-dot" style={{ background: selected.accent }} aria-hidden="true" />
          )}
          {selected?.label ?? value}
        </span>
        <span className={`tselect-caret ${open ? 'open' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="tselect-menu" role="listbox">
          {options.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              className="tselect-option"
              style={o.accent ? ({ '--accent': o.accent } as CSSProperties) : undefined}
              onClick={() => {
                onChange(o.value)
                setOpen(false)
              }}
            >
              {o.accent && <span className="tselect-dot" style={{ background: o.accent }} aria-hidden="true" />}
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
